import { useState } from "react";
import { CaretDownIcon } from "@phosphor-icons/react/CaretDown";
import { Popover } from "@freightsimple/ui-core/Popover/Popover";
import { DevMenu } from "@freightsimple/dev-tools/Menu/DevMenu";
import {
  environment,
  isDemo,
  isLocalOverride,
  isProduction,
} from "@freightsimple/app-shell/environment";

// The carrier portal is external-facing and has no FreightSimple-employee
// concept, so there's no safe way to reveal dev tools in production. Expose them
// in non-production environments only (local + preprod). The engineering
// Developer Settings (inspector / mock / cold-start) stay off until carriers
// wires those subsystems — for now it's the shared Environment badge + Display
// (Viewport + Theme).
function carrierDevToolsEnabled(): boolean {
  return !isProduction() && !isDemo();
}

/**
 * The signed-in identity in the top nav: the carrier account (logo + name) and
 * the self-asserted person using it, opening a menu with who/where they're
 * signed in as and a sign-out ("not you?") action that clears this browser's
 * identity so the next person identifies themselves. In non-prod the menu also
 * hosts the shared dev platform.
 */
export function CarrierUserMenu({
  userName,
  userEmail,
  carrierIdentifier,
  carrierName,
  onSignOut,
}: {
  userName: string;
  userEmail: string;
  carrierIdentifier: string;
  carrierName: string;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          data-testid="carrier-user-menu"
          className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-sm font-medium text-foreground transition-colors hover:bg-white/10"
        >
          {/* Square logo on a white pill so colored marks read on the dark
            bar; a carrier without a logo asset just shows its name. */}
          <img
            src={`/carrierLogosSquare/${carrierIdentifier}.png`}
            alt=""
            className="h-6 w-6 rounded bg-white object-contain p-0.5"
            onError={(event) => {
              event.currentTarget.style.display = "none";
            }}
          />
          <span className="font-semibold">{carrierName}</span>
          <span className="text-content-muted">·</span>
          {userName}
          <CaretDownIcon
            className="h-3 w-3 text-content-secondary"
            weight="bold"
          />
        </button>
      </Popover.Trigger>
      <Popover.Content padding="none" align="end" sideOffset={4}>
        <div className="min-w-48">
          <div className="border-b border-border px-3 py-2">
            <div className="text-xs font-bold text-foreground">{userName}</div>
            <div className="text-[11px] text-muted-foreground">{userEmail}</div>
            <div className="text-[11px] text-muted-foreground">
              {carrierName} · Carrier Portal
            </div>
          </div>
          {carrierDevToolsEnabled() && (
            <DevMenu
              enabled
              tone="hubble"
              environment={environment}
              isLocalOverride={isLocalOverride()}
              showDeveloperSettings={false}
            />
          )}
          <div className="border-t border-border p-1">
            <button
              type="button"
              data-testid="carrier-sign-out"
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
              className="w-full rounded-md px-2 py-1.5 text-left text-xs font-medium text-foreground transition-colors hover:bg-muted"
            >
              Sign out — not {userName.split(" ")[0]}?
            </button>
          </div>
        </div>
      </Popover.Content>
    </Popover>
  );
}
